import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { MapPin } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Navbar } from "@/components/Navbar";
import { PHONE_PLACEHOLDER, ADDRESS_PLACEHOLDER } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { requestLocation, type Coords } from "@/lib/location";
import { reverseGeocode, dialCode, phoneMatchesCountry, type PlaceInfo } from "@/lib/geo";
import { pageHead } from "@/lib/seo";

export const Route = createFileRoute("/complete-profile")({
  head: () => pageHead({ path: "/complete-profile", title: "Complete your profile — Book Swap", description: "Add your name, city and contact details so neighbours know who they're swapping with.", noindex: true }),
  component: CompleteProfile,
});

function CompleteProfile() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [coords, setCoords] = useState<Coords | null>(null);
  const [place, setPlace] = useState<PlaceInfo | null>(null);
  const [locating, setLocating] = useState(false);
  const [saving, setSaving] = useState(false);

  const phoneValid = phoneMatchesCountry(phone, place?.countryCode ?? null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate({ to: "/login" });
      return;
    }
    supabase.from("profiles").select("full_name,phone_number,address,lat,lng").eq("id", user.id).maybeSingle()
      .then(({ data }) => {
        if (!data) return;
        setName(data.full_name ?? "");
        setPhone(data.phone_number ?? "");
        setAddress(data.address ?? "");
        if (data.lat != null && data.lng != null) {
          setCoords({ lat: data.lat, lng: data.lng });
          reverseGeocode(data.lat, data.lng).then((p) => { if (p) setPlace(p); }).catch(() => {});
        }
      });
  }, [user, authLoading, navigate]);

  async function captureLocation() {
    setLocating(true);
    try {
      const c = await requestLocation();
      setCoords(c);
      try {
        const p = await reverseGeocode(c.lat, c.lng);
        if (!p) throw new Error("Place lookup unavailable");
        setPlace(p);
        setAddress(p.formatted);
        const code = dialCode(p.countryCode);
        if (code && !phone.trim().startsWith("+")) setPhone(code + " ");
        toast.success(`Location confirmed: ${p.formatted}`);
      } catch {
        setPlace(null);
        toast.success("Location captured. Please enter your city and international mobile number.");
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Could not get your location.");
    } finally {
      setLocating(false);
    }
  }


  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!user) return;
    if (!name.trim()) return toast.error("Full name is required.");
    if (!coords) return toast.error("Please share your location.");
    if (!phone.trim()) return toast.error("Mobile number is required.");
    if (!phoneValid) return toast.error(place
      ? `Enter a valid ${place.country} mobile number (starting with ${dialCode(place.countryCode)}).`
      : "Enter a valid international mobile number starting with + and its country code.");
    if (!address.trim()) return toast.error("Short address is required.");

    setSaving(true);
    // Upsert (not update) so a missing profile row is created rather than silently no-op'd.
    const { error } = await supabase.from("profiles").upsert({
      id: user.id,
      full_name: name,
      phone_number: phone,
      address,
      lat: coords.lat,
      lng: coords.lng,
    }, { onConflict: "id" });
    if (error) {
      setSaving(false);
      console.error("Profile save error:", error);
      return toast.error("Could not save your profile. Please try again.");
    }
    // Verify the write actually persisted before navigating away.
    const { data: fresh, error: readErr } = await supabase
      .from("profiles")
      .select("full_name,phone_number,address,lat,lng")
      .eq("id", user.id)
      .maybeSingle();
    setSaving(false);
    if (readErr || !fresh || !fresh.phone_number || !fresh.address || fresh.lat == null || fresh.lng == null) {
      console.error("Profile verify failed:", readErr, fresh);
      return toast.error("Saved, but some fields didn't persist. Please try again.");
    }
    toast.success("Profile complete!");
    navigate({ to: "/browse" });
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-md px-4 py-16">
        <div className="rounded-2xl bg-card p-8 shadow-[var(--shadow-card)]">
          <h1 className="text-2xl font-bold">Complete your profile</h1>
          <p className="mt-1 text-sm text-muted-foreground">A few details so neighbours can swap books with you.</p>
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="name">Full name *</Label>
              <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label>Location *</Label>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <Button type="button" variant={coords ? "default" : "outline"} size="sm" onClick={captureLocation} disabled={locating}>
                  <MapPin className="mr-1 h-4 w-4" />
                  {locating ? "Verifying location…" : coords ? "Location captured ✓" : "Use my current location"}
                </Button>
                {place && <span className="text-xs text-muted-foreground">{place.formatted}</span>}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">Used to find nearby books. We fill your city and phone code when place lookup is available.</p>
            </div>
            <div>
              <Label htmlFor="phone">Mobile number *</Label>
              <Input
                id="phone"
                type="tel"
                required
                disabled={!coords}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={place ? `${dialCode(place.countryCode)} …` : PHONE_PLACEHOLDER}
                className={phone && !phoneValid ? "border-destructive" : ""}
              />
              <p className={`mt-1 text-xs ${phone && !phoneValid ? "text-destructive" : "text-muted-foreground"}`}>
                {place
                  ? `Must be a valid ${place.country} number starting with ${dialCode(place.countryCode)}.`
                  : coords ? "Enter the full number starting with + and its country code." : "Available once your location is captured."}
              </p>
            </div>
            <div>
              <Label htmlFor="address">City & country *</Label>
              <Input id="address" required readOnly={!!place} value={address} onChange={(e) => setAddress(e.target.value)} placeholder={place ? ADDRESS_PLACEHOLDER : "Enter your city and country"} />
              <p className="mt-1 text-xs text-muted-foreground">Filled automatically when place lookup is available.</p>
            </div>

            <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={saving}>
              {saving ? "Saving…" : "Save and continue"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
